// VR/XR layer: immersive session lifecycle (VR/AR enter + exit restore), the
// per-controller pick loop and ray/cursor visuals, VR panel layout + gaze
// summon, face-button and joystick-dial polling, the grab bar, and the
// first-person layout walkthrough.
//
// Extracted from main.ts. Host dependencies come in through VRInputCtx;
// everything main.ts needs back is in the returned API. Shared gesture
// infrastructure (lastPick/controllerRays, applyToolAction, handlePanelClick,
// the action/shape mode setters) stays in main.ts — the desktop input path
// drives the same machinery through a synthetic controller 0.
import * as THREE from "three";

import {
  type ControllerRay,
  aimControllerRayAtPanel,
  SELECT_LASER_LENGTH,
  hideControllerRay,
  setControllerRayMarquee,
  setControllerRayOffCanvas,
  updateControllerRayVisuals,
} from "../view/controller-ray";
import {
  type CellBox,
  type PickTarget,
} from "../interaction/pick";
import {
  type VolumeBounds,
  type WorkVolumeSurface,
} from "../scene/work-volume";
import {
  controllerEffectiveWorldPos,
  controllerStates,
  controllers,
  findControllerIndexByHandedness,
  laserLocalDir,
  laserLocalQuat,
  pulseHaptic,
} from "../input/controllers";
import {
  paintPalettePanel,
  palettePanelMesh,
  pickSwatch,
} from "../ui/palette-panel";
import {
  type ToolkitButtonId,
  paintToolkitPanel,
  pickToolkitButton,
  toolkitPanelMesh,
} from "../ui/toolkit-panel";
import {
  type SettingsButtonId,
  paintSettingsPanel,
  pickSettingsButton,
  settingsPanelMesh,
} from "../ui/settings-panel";
import {
  type HelpButtonId,
  helpPanelMesh,
  paintHelpPanel,
  pickHelpButton,
} from "../ui/help-panel";
import { intersectPanelWithSlop } from "../ui/panel-pick";
import { setState, uiState } from "../ui/state";
import {
  initNavigationHandlers,
  resetNavContext,
  setNavContext,
} from "../interaction/handlers/navigation";
import { interaction } from "../interaction/state";
import {
  GRAB_BAR_CEILING_GAP,
  GRAB_BAR_FLOOR_GAP,
  GRAB_BAR_STANDOFF,
  GRAB_BAR_WIDTH,
  EXIT_BTN_HOVER_SCALE,
  exitButtonHitPad,
  exitButtonMesh,
  exitXMesh,
  grabBarGroup,
  grabBarMesh,
  grabBarTargetMesh,
} from "../ui/grab-bar";
import {
  type ActionMode,
  type SelectAction,
  type ShapeMode,
} from "../interaction/tool";
import { createFloorScene } from "../floor/floor-scene";
import {
  WallSystem,
} from "../floor/walls";
import {
  DIAL_DOT_MAX_OFFSET,
  actionDial,
  clampToUnitDisc,
  paintActionDial,
  paintSelectActionDial,
  paintShapeDial,
  positionDial,
  selectActionDial,
  shapeDial,
} from "../ui/dials";

// Per-controller panel hit (refreshed each frame alongside lastPick).
export type PanelHit =
  | { panel: "palette"; swatch: number }
  | { panel: "toolkit"; button: ToolkitButtonId }
  | { panel: "settings"; button: SettingsButtonId }
  | { panel: "help"; button: HelpButtonId }
  | { panel: "grabBar"; distance: number }
  | { panel: "exitButton"; distance: number };

// Everything the VR layer needs from the host app. The pick-result arrays are
// shared by reference — the desktop path fills the same slots via a synthetic
// controller 0. The panel-open flags and transaction owner live in main
// (handlePanelClick writes them), so they come in as getters.
export interface VRInputCtx {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  world: THREE.Group;
  gridGroup: THREE.Group;
  immersiveSkybox: THREE.Mesh;
  floorScene: ReturnType<typeof createFloorScene>;
  wallSystem: WallSystem;
  lastPick: (PickTarget | null)[];
  lastPanelHit: (PanelHit | null)[];
  controllerRays: ControllerRay[];
  resizeHotHandle: number[];
  resizeHotDist: number[];
  resizeHandleMeshes: THREE.Mesh[];
  volumeBounds: VolumeBounds;
  workVolumeSurfaces: WorkVolumeSurface[];
  DESKTOP_BACKGROUND: THREE.Color;
  VR_GRID_POSITION: THREE.Vector3;
  VR_GRID_ROTATION_X: number;
  DESKTOP_GRID_POSITION: THREE.Vector3;
  DESKTOP_GRID_ROTATION_X: number;
  vrGridScale: () => number;
  syncToolbars: () => void;
  frameWorkVolume: () => void;
  frameFloorView: () => void;
  // The layout's world-space bounding box (walls + placements), or null when the
  // layout is empty. Used to center + scale the AR tabletop. Passed as a thunk so
  // main can wire it before its own `layoutBounds` const initialises (TDZ).
  layoutBounds: () => THREE.Box3 | null;
  setActionMode: (mode: ActionMode) => void;
  setShapeMode: (mode: ShapeMode) => void;
  setSelectAction: (mode: SelectAction) => void;
  modelUndo: () => void;
  modelRedo: () => void;
  grabbedHandleIndex: (axis: 0 | 1 | 2, sign: -1 | 1) => number;
  gestureRaycastMasks: (i: number) => {
    skip: ReadonlySet<string> | undefined;
    include: ReadonlySet<string> | undefined;
    includeBox: CellBox | null;
    skipBox?: CellBox | null;
  };
  multiObjectPick: (
    i: number,
    originWorld: THREE.Vector3,
    dirWorld: THREE.Vector3,
    skip: ReadonlySet<string> | undefined,
    include: ReadonlySet<string> | undefined,
    includeBox: CellBox | null,
    skipBox?: CellBox | null,
  ) => { pick: PickTarget; group: THREE.Object3D };
  dominantControllerIndex: () => number;
  nonDominantControllerIndex: () => number;
  settingsOpen: () => boolean;
  helpOpen: () => boolean;
  txnOwner: () => number | null;
}

export function installVRInput(ctx: VRInputCtx) {
  const {
    renderer,
    scene,
    camera,
    world,
    gridGroup,
    immersiveSkybox,
    floorScene,
    wallSystem,
    lastPick,
    lastPanelHit,
    controllerRays,
    resizeHotHandle,
    resizeHotDist,
    resizeHandleMeshes,
    volumeBounds,
    workVolumeSurfaces,
    DESKTOP_BACKGROUND,
    VR_GRID_POSITION,
    VR_GRID_ROTATION_X,
    DESKTOP_GRID_POSITION,
    DESKTOP_GRID_ROTATION_X,
    vrGridScale,
    syncToolbars,
    frameWorkVolume,
    frameFloorView,
    layoutBounds,
    setActionMode,
    setShapeMode,
    setSelectAction,
    modelUndo,
    modelRedo,
    grabbedHandleIndex,
    gestureRaycastMasks,
    multiObjectPick,
    dominantControllerIndex,
    nonDominantControllerIndex,
  } = ctx;

// ── VR Session ───────────────────────────────────────────────────────────

const vrButton = document.getElementById("vr-button") as HTMLButtonElement;
const walkthroughButton = document.getElementById(
  "walkthrough-button",
) as HTMLButtonElement;
// The immersive mode the "VR edit" button uses (AR preferred, else VR), or
// null. The walkthrough always forces fully-immersive VR (no passthrough).
let vrEnterMode: XRSessionMode | null = null;
let immersiveVrSupported = false;

// Enter an immersive session. `walkthroughMode` chooses the LAYOUT first-person
// walkthrough (full-scale room, teleport locomotion) instead of the default
// model-on-a-table experience. The walkthrough always runs as immersive-VR — you
// stand in the modelled environment, never see-through AR — while the plain
// "Enter VR/AR" button uses whatever the device prefers.
function requestVRSession(walkthroughMode: boolean): void {
  const mode: XRSessionMode | null = walkthroughMode
    ? immersiveVrSupported
      ? "immersive-vr"
      : null
    : vrEnterMode;
  if (!mode) return;
  // "VR edit" from Layout mode is its own experience: the layout sits on the
  // desk in front of you in passthrough AR (no voxel cube, no grab bar). Capture
  // it once so the session-end handler tears down the same thing it set up, even
  // if the desktop mode somehow changes underneath.
  const layoutTabletop = !walkthroughMode && uiState.mode === "floorplan";
  // The walkthrough's presentation rig (IBL + tone mapping + shadows + sky)
  // costs far more per pixel than the flat editing rig, and a Quest that
  // misses frame rate falls into compositor reprojection — the world swims
  // and tilts with head motion. Buy the shading budget back with a smaller
  // framebuffer (1.5 is still sharp) — must be set BEFORE the session starts.
  renderer.xr.setFramebufferScaleFactor(walkthroughMode ? 1.5 : 2.0);
  const xr = (navigator as Navigator & { xr: XRSystem }).xr;
  xr.requestSession(mode, {
    optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking"],
  }).then((session) => {
    renderer.xr.setSession(session);
    vrButton.disabled = true;
    walkthroughButton.disabled = true;
    world.position.set(0, 0, 0);
    world.rotation.set(0, 0, 0);
    world.scale.set(1, 1, 1);
    scene.background = null;
    immersiveSkybox.visible = mode !== "immersive-ar";
    setState("passthroughActive", mode === "immersive-ar");
    if (walkthroughMode) {
      enterWalkthrough();
    } else if (layoutTabletop) {
      enterLayoutTabletop();
    } else {
      gridGroup.position.copy(VR_GRID_POSITION);
      gridGroup.rotation.x = VR_GRID_ROTATION_X;
      gridGroup.scale.setScalar(vrGridScale());
      grabBarGroup.visible = true;
    }
    syncToolbars();
    session.addEventListener("end", () => {
      vrButton.disabled = false;
      walkthroughButton.disabled = !immersiveVrSupported;
      if (walkthroughMode) exitWalkthrough();
      else if (layoutTabletop) exitLayoutTabletop();
      syncToolbars();
      grabBarGroup.visible = false;
      scene.background = DESKTOP_BACKGROUND;
      immersiveSkybox.visible = false;
      gridGroup.position.copy(DESKTOP_GRID_POSITION);
      gridGroup.rotation.x = DESKTOP_GRID_ROTATION_X;
      gridGroup.scale.setScalar(1);
      world.position.set(0, 0, 0);
      world.rotation.set(0, 0, 0);
      world.scale.set(1, 1, 1);
      if (uiState.mode === "floorplan") frameFloorView();
      else frameWorkVolume();
    });
  }).catch((e) => {
    // Session request denied/failed (permissions, another session active) —
    // leave the page usable and the buttons pressable instead of wedged.
    vrButton.disabled = false;
    walkthroughButton.disabled = !immersiveVrSupported;
    console.warn("Immersive session request failed:", e);
  });
}

if ("xr" in navigator) {
  const xr = (navigator as Navigator & { xr: XRSystem }).xr;
  Promise.all([
    xr.isSessionSupported("immersive-ar").catch(() => false),
    xr.isSessionSupported("immersive-vr").catch(() => false),
  ]).then(([arSupported, vrSupported]) => {
    immersiveVrSupported = vrSupported;
    vrEnterMode = arSupported
      ? "immersive-ar"
      : vrSupported
        ? "immersive-vr"
        : null;
    // "VR edit" enters whenever AR or VR is available. The walkthrough needs
    // fully-immersive VR (it's not an AR experience). Buttons keep their icon
    // label and stay disabled/grayed when their mode is unsupported.
    if (vrEnterMode) {
      vrButton.disabled = false;
      vrButton.addEventListener("click", () => requestVRSession(false));
    }
    if (vrSupported) {
      walkthroughButton.disabled = false;
      walkthroughButton.addEventListener("click", () => requestVRSession(true));
    }
  });
}

// Scratch vectors for the per-frame pick — kept module-scoped so the
// render loop doesn't allocate.
const _pickOriginTmp = new THREE.Vector3();
const _pickDirTmp = new THREE.Vector3();
const _laserLocalQuatTmp = new THREE.Quaternion();
const _panelRaycaster = new THREE.Raycaster();
// Reused per frame for the laser wall-truncation raycast (hover safety net).
const _visibleWallList: THREE.Object3D[] = [];
const _offCanvasPosTmp = new THREE.Vector3();
const _camWorldPosTmp = new THREE.Vector3();
const _grabBarWorldPosTmp = new THREE.Vector3();
const _canvasLocalTmp = new THREE.Vector3();
const _canvasScaleTmp = new THREE.Vector3();
const _marqueeCornerTmp = new THREE.Vector3();
const _marqueeCtrlTmp = new THREE.Vector3();

// Empty-space cursor: the laser parks on an imaginary sphere of radius
// (controller→grab bar distance) around the controller, and disappears
// entirely if that virtual hit point is more than 10 inches outside the
// canvas. CANVAS_AIM_LIMIT is that threshold.
const CANVAS_AIM_LIMIT = 10 * 0.0254;

// Slop band past a summoned panel's visible edge that still counts as
// a panel hit (cursor-only — buttons require an actual mesh hit). Sized
// to comfortably exceed the disc cursor's apparent radius at typical
// arm's-length panel distance, so the cursor disappears only once its
// outer edge fully clears the panel.
const PANEL_CURSOR_SLOP = 0.01;

// World-space distance from `worldPoint` to the nearest point on the
// canvas (cube). Works correctly under gridGroup rotation + uniform
// scale: transforms the point into grid-local, clamps to the cube's
// AABB there, then scales the local-space gap back to world.
function distanceFromPointToCanvas(worldPoint: THREE.Vector3): number {
  gridGroup.updateMatrixWorld(true);
  _canvasLocalTmp.copy(worldPoint);
  gridGroup.worldToLocal(_canvasLocalTmp);
  const cx = THREE.MathUtils.clamp(
    _canvasLocalTmp.x,
    volumeBounds.minX,
    volumeBounds.maxX,
  );
  const cy = THREE.MathUtils.clamp(_canvasLocalTmp.y, 0, volumeBounds.height);
  const cz = THREE.MathUtils.clamp(
    _canvasLocalTmp.z,
    volumeBounds.minZ,
    volumeBounds.maxZ,
  );
  const dx = _canvasLocalTmp.x - cx;
  const dy = _canvasLocalTmp.y - cy;
  const dz = _canvasLocalTmp.z - cz;
  const localDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  gridGroup.getWorldScale(_canvasScaleTmp);
  return localDist * _canvasScaleTmp.x;
}


// Hover "hot" state per panel for the end-of-frame repaint — the first
// controller to land on a button each frame claims the highlight. Reset at
// the top of updatePickAndVisuals (module-scoped to avoid per-frame allocs).
const _panelHot = {
  toolkit: null as ToolkitButtonId | null,
  palette: null as number | null,
  settings: null as SettingsButtonId | null,
  help: null as HelpButtonId | null,
};

// The laser-targetable UI panels in hit-test priority order: the toolkit is
// the rarer summon, so it "wins" the laser over the palette when both are
// visible (toggling it stays easy; the user almost never holds A + B at
// once). resolve() maps a UV hit to that panel's PanelHit (null = dead zone)
// and records the hover highlight. Closures are built once, not per frame.
const PANEL_TARGETS: {
  mesh: THREE.Mesh;
  resolve: (u: number, v: number) => PanelHit | null;
}[] = [
  {
    mesh: toolkitPanelMesh,
    resolve: (u, v) => {
      const btn = pickToolkitButton(u, v);
      if (btn === null) return null;
      if (_panelHot.toolkit === null) _panelHot.toolkit = btn;
      return { panel: "toolkit", button: btn };
    },
  },
  {
    mesh: palettePanelMesh,
    resolve: (u, v) => {
      const swatch = pickSwatch(u, v);
      if (swatch === null) return null;
      if (_panelHot.palette === null) _panelHot.palette = swatch;
      return { panel: "palette", swatch };
    },
  },
  {
    mesh: settingsPanelMesh,
    resolve: (u, v) => {
      const btn = pickSettingsButton(u, v);
      if (btn === null) return null;
      if (_panelHot.settings === null) _panelHot.settings = btn;
      return { panel: "settings", button: btn };
    },
  },
  {
    mesh: helpPanelMesh,
    resolve: (u, v) => {
      const btn = pickHelpButton(u, v);
      if (btn === null) return null;
      if (_panelHot.help === null) _panelHot.help = btn;
      return { panel: "help", button: btn };
    },
  },
];

// Per-frame: raycast from each connected controller, stash the result
// in lastPick[i] / lastPanelHit[i], and update the ray + cursor visuals.
// UI panels take priority over voxel/surface picks when visible, so the
// user can aim through whatever's in front of them at the buttons.
function updatePickAndVisuals(): void {
  _panelHot.toolkit = null;
  _panelHot.palette = null;
  _panelHot.settings = null;
  _panelHot.help = null;

  // Camera world position drives the off-canvas disc cursor's
  // billboarding. Sample once per frame; both controllers share it.
  const headCam = renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
  headCam.getWorldPosition(_camWorldPosTmp);

  for (let i = 0; i < controllers.length; i++) {
    const c = controllers[i];
    const ray = controllerRays[i];
    if (!controllerStates[i].inputSource) {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      hideControllerRay(ray);
      continue;
    }
    // Non-dominant hand is intentionally laser-less. Drawing / picking
    // is single-hand by design (see Settings → Dominant hand). The
    // off-hand still drives joystick dials, undo/redo face buttons,
    // and the palm grip for two-grip nav — but no laser, no trigger
    // action, and no squeeze-tilt (there's nothing to tilt).
    if (i === nonDominantControllerIndex()) {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      hideControllerRay(ray);
      continue;
    }
    // Squeeze on this controller tilts the laser down 45°. The line
    // mesh rotates around its local X axis so the visual stays in
    // sync with the pick direction used below; negative rotation.x
    // maps the line's -Z direction toward -Y (down).
    //
    // Suppressed during gestures that already own the squeeze: two-
    // grip nav (both controllers squeezing) and canvas-drag (single
    // controller dragging the grab bar). Otherwise the laser would
    // tilt mid-gesture and confuse the visual.
    // Two-grip nav: hide both lasers + cursors entirely. The user is
    // panning/rotating/scaling the canvas; a visible cursor would
    // imply they're still targeting something for selection, which
    // they're not.
    if (interaction.kind === "navigate") {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      hideControllerRay(ray);
      continue;
    }
    const tiltSuppressed =
      interaction.kind === "canvas_drag" &&
      interaction.controllerIndex === i;
    // ray.line is parented to the controller, so its local quaternion
    // composes with controller.worldQuat to produce its world rotation.
    // EXPERIMENT: the visible laser line ignores precision-aim damping
    // (`ignorePrecision`) and stays rigidly attached to the raw controller
    // (tilt only) — so in precision mode the laser follows the controller
    // while the cursor/pick below still uses the dampened aim. The line
    // origin stays at the controller (zero offset) to match. When precision
    // isn't engaged this is identical to the raw aim, so nothing changes.
    laserLocalQuat(i, _laserLocalQuatTmp, {
      suppressTilt: tiltSuppressed,
      ignorePrecision: true,
    });
    ray.line.quaternion.copy(_laserLocalQuatTmp);
    ray.line.position.set(0, 0, 0);
    // Active canvas-drag: the bar IS the cursor, so hide the laser +
    // both world cursors entirely. Showing a stub laser here also
    // produced a visual snap when the user grabbed the bar with the
    // squeeze-tilted laser — the tilt is suppressed during drag and
    // the laser would jump back to horizontal for a frame.
    if (
      interaction.kind === "canvas_drag" &&
      interaction.controllerIndex === i
    ) {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      hideControllerRay(ray);
      continue;
    }
    // Active marquee drag: stretch the laser so its tip lands exactly on
    // the box's current (dragged) corner — the laser-on-plane point — so
    // the line stays connected to the selection window as it grows or
    // shrinks. Returns to the regular SELECT stub once the gesture ends
    // (this branch stops applying and the normal pick path takes over).
    if (
      interaction.kind === "marquee_select" &&
      interaction.controllerIndex === i
    ) {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      _marqueeCornerTmp
        .copy(interaction.planeOrigin)
        .addScaledVector(interaction.planeRight, interaction.currentR)
        .addScaledVector(interaction.planeUp, interaction.currentU);
      c.getWorldPosition(_marqueeCtrlTmp);
      setControllerRayMarquee(
        ray,
        _marqueeCtrlTmp.distanceTo(_marqueeCornerTmp),
      );
      continue;
    }
    // RESIZE mode: aim at a wall handle and drag it. Raycast the handle
    // spheres (the walls themselves get occlusion-culled) and skip the
    // voxel pick. During an active drag the laser locks to the grabbed
    // handle so it stays attached as the wall moves. Skipped while a panel
    // is open so the toolkit (which is how you leave resize mode) still
    // takes the laser.
    if (
      uiState.topMode === "resize" &&
      !toolkitPanelMesh.visible &&
      !palettePanelMesh.visible &&
      !settingsPanelMesh.visible &&
      !helpPanelMesh.visible
    ) {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      controllerEffectiveWorldPos(i, _pickOriginTmp);
      laserLocalDir(i, _pickDirTmp);
      _pickDirTmp.transformDirection(c.matrixWorld).normalize();

      if (interaction.kind === "resize_wall" && interaction.controllerIndex === i) {
        const idx = grabbedHandleIndex(interaction.axis, interaction.sign);
        resizeHotHandle[i] = idx;
        resizeHandleMeshes[idx].getWorldPosition(_offCanvasPosTmp);
        setControllerRayOffCanvas(
          ray,
          _offCanvasPosTmp,
          _camWorldPosTmp,
          _pickOriginTmp.distanceTo(_offCanvasPosTmp),
        );
        continue;
      }

      _panelRaycaster.set(_pickOriginTmp, _pickDirTmp);
      const hits = _panelRaycaster.intersectObjects(resizeHandleMeshes, false);
      if (hits.length > 0) {
        resizeHotHandle[i] = resizeHandleMeshes.indexOf(hits[0].object as THREE.Mesh);
        resizeHotDist[i] = hits[0].distance;
        setControllerRayOffCanvas(ray, hits[0].point, _camWorldPosTmp, hits[0].distance);
      } else {
        resizeHotHandle[i] = -1;
        // Laser pointing forward so the user can sweep to find a handle.
        _offCanvasPosTmp.copy(_pickDirTmp).multiplyScalar(0.6).add(_pickOriginTmp);
        setControllerRayOffCanvas(ray, _offCanvasPosTmp, _camWorldPosTmp, 0.6);
      }
      continue;
    }
    // Extrude (draw + erase) flows through the normal pick path below —
    // its cursor is just the standard face cursor on whatever the laser
    // hits. ERASE picks against the original object treated as still
    // present (voxelRaycastInclude = editedKeys, wired below), so the
    // cursor stays on the ghost; DRAW picks against the live grid, so the
    // cursor rides the leading placed face. Neither is axis-locked.

    // Pick origin + direction both flow through the precision-aim
    // helpers so dampening applies uniformly. Without precision active,
    // these reduce to the controller's actual world transform.
    controllerEffectiveWorldPos(i, _pickOriginTmp);
    laserLocalDir(i, _pickDirTmp);
    _pickDirTmp.transformDirection(c.matrixWorld).normalize();

    // Try the toolkit panel first, then the palette panel — toolkit is
    // a more rarely-summoned target, so when both are visible we let
    // the toolkit "win" the laser to make toggling it easier. (In
    // practice the user almost never holds A and B at the same time.)
    //
    // Hit test uses a slop margin past the visible panel edge so the
    // disc cursor stays drawn while its outer rim is still touching
    // the panel. Buttons only fire when the laser is inside the actual
    // mesh (uv !== null); the slop band is cursor-only.
    let panelHandled = false;
    for (const target of PANEL_TARGETS) {
      if (!target.mesh.visible) continue;
      const hit = intersectPanelWithSlop(
        target.mesh,
        PANEL_CURSOR_SLOP,
        _pickOriginTmp,
        _pickDirTmp,
      );
      if (!hit) continue;
      lastPick[i] = null;
      lastPanelHit[i] = hit.uv ? target.resolve(hit.uv.x, hit.uv.y) : null;
      aimControllerRayAtPanel(ray, hit.worldHit, _camWorldPosTmp, hit.distance);
      panelHandled = true;
      break;
    }
    if (panelHandled) continue;

    // A panel is summoned but the laser isn't on it (nor in its slop
    // band). Suppress every other hover target — grab bar, voxel,
    // empty-space cursor — so the cursor can't escape onto the canvas
    // behind the panel. Releasing A/B drops panel visibility and the
    // normal pick path resumes the next frame.
    if (
      toolkitPanelMesh.visible ||
      palettePanelMesh.visible ||
      settingsPanelMesh.visible ||
      helpPanelMesh.visible
    ) {
      lastPick[i] = null;
      lastPanelHit[i] = null;
      hideControllerRay(ray);
      continue;
    }

    // Grab bar + exit button live outside the cube on a perch that the
    // user can almost always reach. Raycast against them and the voxel
    // pick separately; whichever is closer to the controller wins, so
    // a voxel directly in front of the bar isn't swallowed by the bar's
    // generous hit pad.
    let grabBarHit: PanelHit | null = null;
    let grabBarDistance = Infinity;
    _panelRaycaster.set(_pickOriginTmp, _pickDirTmp);
    const grabHits = _panelRaycaster.intersectObjects(
      [grabBarTargetMesh, exitButtonHitPad],
      false,
    );
    if (grabHits.length > 0) {
      const h = grabHits[0];
      grabBarDistance = h.distance;
      grabBarHit =
        h.object === exitButtonHitPad
          ? { panel: "exitButton", distance: h.distance }
          : { panel: "grabBar", distance: h.distance };
    }

    // DRAW gestures (voxel_paint, box_draw) pass their placed cells
    // as voxelRaycastSkip so the laser looks past freshly-placed
    // voxels and keeps targeting the original surface.
    //
    // box_erase passes its placedKeys as voxelRaycastInclude (the
    // inverse) so the laser stops at the deleted face — the cursor
    // sits on the just-erased voxel's position instead of drilling
    // through to whatever sits behind it. Wall-following emerges
    // naturally when the user aims at a side face whose laser path
    // doesn't pass through any already-erased cell.
    const masks = gestureRaycastMasks(i);
    const { pick, group: pickObjectGroup } = multiObjectPick(
      i,
      _pickOriginTmp,
      _pickDirTmp,
      masks.skip,
      masks.include,
      masks.includeBox,
    );
    // Render the face cursor in the target object's frame.
    if (ray.cursor.parent !== pickObjectGroup) pickObjectGroup.add(ray.cursor);

    if (grabBarHit && (!pick || grabBarDistance < pick.distance)) {
      lastPick[i] = null;
      lastPanelHit[i] = grabBarHit;
      // World hit point = controller origin + dir × hit distance.
      _offCanvasPosTmp
        .copy(_pickDirTmp)
        .multiplyScalar(grabBarDistance)
        .add(_pickOriginTmp);
      setControllerRayOffCanvas(
        ray,
        _offCanvasPosTmp,
        _camWorldPosTmp,
        grabBarDistance,
      );
    } else if (pick) {
      lastPanelHit[i] = null;
      lastPick[i] = pick;
      // Cap the laser at the closest visible wall in its path, in
      // addition to pick.distance. pickRay already prefers a closer
      // surface over a farther voxel, but the surface raycast skips
      // hits at voxel-filled cells — so a voxel touching the inside
      // of a wall can leave the wall un-picked even though it's the
      // closest visible geometry the laser passes through. Without
      // this cap the laser visually extends past the wall to the
      // voxel face beyond it.
      _visibleWallList.length = 0;
      for (const s of workVolumeSurfaces) {
        if (s.mesh.visible) _visibleWallList.push(s.mesh);
      }
      _panelRaycaster.set(_pickOriginTmp, _pickDirTmp);
      const wallHits = _panelRaycaster.intersectObjects(
        _visibleWallList,
        false,
      );
      let laserDist = pick.distance;
      if (wallHits.length > 0 && wallHits[0].distance < laserDist) {
        laserDist = wallHits[0].distance;
      }
      // SELECT tool: shorter stub (the "marquee laser") and no face-cell
      // cursor — the laser just points; the selection window (during a
      // drag) is the only grid-anchored visual.
      const selectMode = uiState.topMode === "select";
      updateControllerRayVisuals(
        ray,
        pick,
        laserDist,
        selectMode ? SELECT_LASER_LENGTH : undefined,
      );
      if (selectMode) ray.cursor.visible = false;
    } else {
      // Empty space. Cast onto an imaginary sphere of radius |controller
      // → grab bar| so the cursor sits at a comfortable depth no matter
      // which way the user sweeps the laser. If that virtual hit lands
      // more than 10 in outside the canvas, the user clearly isn't aiming
      // at anything actionable — hide the laser + cursor entirely.
      lastPanelHit[i] = null;
      lastPick[i] = null;
      grabBarMesh.getWorldPosition(_grabBarWorldPosTmp);
      const aimDist = _pickOriginTmp.distanceTo(_grabBarWorldPosTmp);
      _offCanvasPosTmp
        .copy(_pickDirTmp)
        .multiplyScalar(aimDist)
        .add(_pickOriginTmp);
      if (distanceFromPointToCanvas(_offCanvasPosTmp) > CANVAS_AIM_LIMIT) {
        hideControllerRay(ray);
      } else {
        setControllerRayOffCanvas(
          ray,
          _offCanvasPosTmp,
          _camWorldPosTmp,
          aimDist,
          uiState.topMode === "select" ? SELECT_LASER_LENGTH : undefined,
        );
      }
    }
  }

  if (palettePanelMesh.visible) {
    paintPalettePanel(uiState.currentPaletteIndex, _panelHot.palette);
  }
  if (toolkitPanelMesh.visible) {
    paintToolkitPanel({
      topMode: uiState.topMode,
      gridOn: uiState.gridLinesVisible,
      edgesOn: uiState.edgesVisible,
      hot: _panelHot.toolkit,
    });
  }
  if (settingsPanelMesh.visible) {
    paintSettingsPanel({
      dominantHand: uiState.dominantHand,
      passthroughOn: uiState.passthroughActive,
      hot: _panelHot.settings,
    });
  }
  if (helpPanelMesh.visible) {
    paintHelpPanel({
      dominantHand: uiState.dominantHand,
      topMode: uiState.topMode,
      hot: _panelHot.help,
    });
  }

  // Exit-button hover-scale. Aggregate hover across both controllers, then
  // ease toward 1.5× when hovered, back to 1.0× when not. Lerp factor 0.25
  // gives a quick but visible "pop" at the typical 90 Hz frame rate.
  const exitHovered =
    lastPanelHit[0]?.panel === "exitButton" ||
    lastPanelHit[1]?.panel === "exitButton";
  const exitTarget = exitHovered ? EXIT_BTN_HOVER_SCALE : 1.0;
  const lerped =
    exitButtonMesh.scale.x + (exitTarget - exitButtonMesh.scale.x) * 0.25;
  exitButtonMesh.scale.setScalar(lerped);
  // X glyph only shows when the user is aiming at the button.
  exitXMesh.visible = exitHovered;
}

// ── Face-button polls + panel summons ────────────────────────────────────


// Both controllers' face buttons + analog squeeze are polled here
// since these fields are per-slot, not handedness-specific.
function pollFaceButtons(): void {
  const session = renderer.xr.getSession();
  if (!session) {
    for (const cs of controllerStates) {
      cs.aHeld = false;
      cs.bHeld = false;
      cs.squeezeValue = 0;
    }
    return;
  }
  for (const src of session.inputSources) {
    if (!src.gamepad) continue;
    const idx = findControllerIndexByHandedness(src.handedness);
    if (idx < 0) continue;
    controllerStates[idx].aHeld = src.gamepad.buttons[4]?.pressed ?? false;
    controllerStates[idx].bHeld = src.gamepad.buttons[5]?.pressed ?? false;
    // Analog grip pressure (Meta Touch: buttons[1].value). The
    // boolean `squeezing` flag (from squeezestart/end events) is
    // still maintained by the controller event handlers — this is
    // just the per-frame pressure read.
    controllerStates[idx].squeezeValue =
      src.gamepad.buttons[1]?.value ?? 0;
  }
}

// Non-dominant face buttons drive undo/redo: south button (X on a left
// controller, A on a right controller) = undo; north button (Y / B) =
// redo. Rising-edge only so a held button doesn't spam the history.
// Gated on idle so an in-flight drag can't be undone out from under
// itself.
let undoBtnPrev = false;
let redoBtnPrev = false;
function pollUndoRedo(): void {
  const idx = nonDominantControllerIndex();
  if (idx < 0) {
    undoBtnPrev = false;
    redoBtnPrev = false;
    return;
  }
  const undoBtn = controllerStates[idx].aHeld;
  const redoBtn = controllerStates[idx].bHeld;
  if (interaction.kind === "idle" && ctx.txnOwner() === null) {
    if (undoBtn && !undoBtnPrev && modelUndo()) pulseHaptic(idx, 0.5, 20);
    if (redoBtn && !redoBtnPrev && modelRedo()) pulseHaptic(idx, 0.5, 20);
  }
  undoBtnPrev = undoBtn;
  redoBtnPrev = redoBtn;
}

const _panelGazePos = new THREE.Vector3();
const _panelGazeFwd = new THREE.Vector3();
const _eyeLTmp = new THREE.Vector3();
const _eyeRTmp = new THREE.Vector3();

// True head center for placing head-anchored UI. three's XR "union"
// camera (renderer.xr.getCamera()) is derived from the LEFT eye plus an
// approximate horizontal offset toward center; on devices with
// asymmetric per-eye frustums (e.g. Vision Pro's canted displays) that
// offset isn't exactly half-IPD, leaving the union camera — and anything
// placed in front of it — biased slightly to one side. Averaging the two
// eye world positions gives the exact midpoint, so summoned UI lands
// dead center. Falls back to the camera position outside XR / mono.
function headCenterWorld(out: THREE.Vector3): void {
  if (renderer.xr.isPresenting) {
    const xr = renderer.xr.getCamera();
    if (xr.cameras.length === 2) {
      _eyeLTmp.setFromMatrixPosition(xr.cameras[0].matrixWorld);
      _eyeRTmp.setFromMatrixPosition(xr.cameras[1].matrixWorld);
      out.copy(_eyeLTmp).add(_eyeRTmp).multiplyScalar(0.5);
      return;
    }
    xr.getWorldPosition(out);
    return;
  }
  camera.getWorldPosition(out);
}
let aHeldPrev = false;
let bHeldPrev = false;
// Which face button currently owns the panel summon slot, if any.
// First press wins until release — the other button is ignored while
// one panel is up, so the two can't be summoned simultaneously.
let panelSummonOwner: "toolkit" | "palette" | null = null;

// Panel summon: snap the requested panel in front of the user on the
// button's rising edge, freeze it there until release. The panel stays
// world-fixed during the hold so small head jitter doesn't drift the
// buttons under the laser.
function summonPanelAtGaze(
  mesh: THREE.Mesh,
  dropOffset: number,
): void {
  const cam = renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
  // Anchor at the true head center (eye midpoint), not the union camera's
  // slightly side-biased position — otherwise the panel lands off-center.
  headCenterWorld(_panelGazePos);
  _panelGazeFwd.set(0, 0, -1).transformDirection(cam.matrixWorld);
  const SUMMON_DIST = 0.8;
  mesh.position
    .copy(_panelGazePos)
    .addScaledVector(_panelGazeFwd, SUMMON_DIST);
  mesh.position.y += dropOffset;
  mesh.lookAt(_panelGazePos);
}

function updateUIPanelsLayout(): void {
  const di = dominantControllerIndex();
  const aHeld = di >= 0 ? controllerStates[di].aHeld : false;
  const bHeld = di >= 0 ? controllerStates[di].bHeld : false;

  // Rising edge claims the summon slot only when no panel is currently
  // owning it. Toolkit lifts 5cm above eye-line on summon; palette
  // drops 15cm below — vertical offsets stay even though only one is
  // up at a time, so the position matches user muscle memory.
  if (aHeld && !aHeldPrev && panelSummonOwner === null) {
    panelSummonOwner = "toolkit";
    summonPanelAtGaze(toolkitPanelMesh, -0.3);
  }
  if (bHeld && !bHeldPrev && panelSummonOwner === null) {
    panelSummonOwner = "palette";
    summonPanelAtGaze(palettePanelMesh, -0.15);
  }

  // Owning button release frees the slot. The other button (if held)
  // does NOT auto-take over — the user has to release and re-press to
  // summon the other panel, which matches the rising-edge gating.
  if (panelSummonOwner === "toolkit" && !aHeld) panelSummonOwner = null;
  if (panelSummonOwner === "palette" && !bHeld) panelSummonOwner = null;

  toolkitPanelMesh.visible = panelSummonOwner === "toolkit";
  palettePanelMesh.visible = panelSummonOwner === "palette";
  // Settings + help panels are sticky (state owned by their *PanelOpen
  // flags), not gated on a face button.
  settingsPanelMesh.visible = ctx.settingsOpen();
  helpPanelMesh.visible = ctx.helpOpen();

  aHeldPrev = aHeld;
  bHeldPrev = bHeld;
}

// Two-hand navigation is the only handler ported in phase 1. Provides
// grip-grip pan/rotate/zoom of the cube so the user can frame it in VR.
initNavigationHandlers(
  world,
  gridGroup,
  (out) => {
    const cam = renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
    cam.getWorldPosition(out);
  },
  grabBarMesh,
);

// ── Joystick Dials ───────────────────────────────────────────────────────
//
// HUD-style wheels summoned at the user's view by joystick deflection.
// The dial set depends on uiState.topMode:
//
//   SKETCH:
//     • Left stick → action dial (DRAW / ERASE). Push horizontally.
//     • Right stick → shape dial (BOX / VOXEL / EXTRUDE). Push toward
//       a wedge.
//
//   SELECT:
//     • Left stick → inert (no dial).
//     • Right stick → select-action dial (MOVE / DELETE). Push left or
//       right to pick. Sets what the trigger does when it lands on a
//       selected voxel (trigger off-selection always starts a marquee).
//
// Mutually exclusive: whichever stick crosses the deadzone first wins
// for this gesture cycle; the other is locked out until the winning
// stick releases below the deadzone. Picks commit on release, with a
// haptic pulse on each wedge change so the user can feel the selection
// without staring at the dial.

const DIAL_DEADZONE = 0.5;
let dialPendingAction: ActionMode = uiState.actionMode;
let dialPendingShape: ShapeMode = uiState.shapeMode;
let dialPendingSelectAction: SelectAction = uiState.selectAction;
let currentDial: "action" | "shape" | "selectAction" | null = null;

paintActionDial(uiState.actionMode);
paintShapeDial(uiState.shapeMode);
paintSelectActionDial(uiState.selectAction);

function activeDialCamera(): THREE.Camera {
  return renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
}


function pollDials(): void {
  const session = renderer.xr.getSession();
  if (!session) return;
  let leftX = 0;
  let leftY = 0;
  let rightX = 0;
  let rightY = 0;
  for (const src of session.inputSources) {
    if (!src.gamepad) continue;
    const ax = src.gamepad.axes[2] ?? 0;
    const ay = src.gamepad.axes[3] ?? 0;
    if (src.handedness === "left") {
      leftX = ax;
      leftY = ay;
    } else if (src.handedness === "right") {
      rightX = ax;
      rightY = ay;
    }
  }
  // Alias the raw left/right gamepad reads to non-dominant/dominant so
  // the rest of the dial logic doesn't have to know which physical
  // controller it's reading. Non-dominant drives the action dial,
  // dominant drives the shape / select-action dial.
  const dominantIsRight = uiState.dominantHand === "right";
  const nonDomX = dominantIsRight ? leftX : rightX;
  const nonDomY = dominantIsRight ? leftY : rightY;
  const domX = dominantIsRight ? rightX : leftX;
  const domY = dominantIsRight ? rightY : leftY;
  // SELECT mode ignores the non-dominant stick — the action dial isn't
  // active there, so suppress its deflection from the acquire-lock
  // check.
  const nonDomEnabled = uiState.topMode === "sketch";
  const nonDomActive =
    nonDomEnabled && Math.hypot(nonDomX, nonDomY) >= DIAL_DEADZONE;
  // RESIZE mode has no dials; gating both sticks off here lets the
  // existing release logic hide any dial that was showing.
  const domEnabled = uiState.topMode !== "resize";
  const domActive = domEnabled && Math.hypot(domX, domY) >= DIAL_DEADZONE;
  const domDialKind: "shape" | "selectAction" =
    uiState.topMode === "select" ? "selectAction" : "shape";

  // Acquire the lock on first deflection.
  if (currentDial === null) {
    if (nonDomActive) {
      currentDial = "action";
      dialPendingAction = uiState.actionMode;
      paintActionDial(dialPendingAction);
      positionDial(actionDial, activeDialCamera());
      actionDial.group.visible = true;
    } else if (domActive) {
      currentDial = domDialKind;
      if (domDialKind === "shape") {
        dialPendingShape = uiState.shapeMode;
        paintShapeDial(dialPendingShape);
        positionDial(shapeDial, activeDialCamera());
        shapeDial.group.visible = true;
      } else {
        dialPendingSelectAction = uiState.selectAction;
        paintSelectActionDial(dialPendingSelectAction);
        positionDial(selectActionDial, activeDialCamera());
        selectActionDial.group.visible = true;
      }
    }
  }

  if (currentDial === "action") {
    if (nonDomActive) {
      const next: ActionMode = nonDomX > 0 ? "draw" : "erase";
      if (next !== dialPendingAction) {
        dialPendingAction = next;
        paintActionDial(next);
        const idx = nonDominantControllerIndex();
        if (idx >= 0) pulseHaptic(idx, 0.4, 15);
      }
      const c = clampToUnitDisc(nonDomX, nonDomY);
      actionDial.dot.position.set(
        c.x * DIAL_DOT_MAX_OFFSET,
        -c.y * DIAL_DOT_MAX_OFFSET,
        0.001,
      );
    } else {
      setActionMode(dialPendingAction);
      actionDial.group.visible = false;
      currentDial = null;
    }
  } else if (currentDial === "shape") {
    if (domActive) {
      // atan2(x, -y): 0 = up, increasing clockwise. Four 90° wedges matching the
      // dial art: up = BOX, right = VOXEL, down = CIRCLE, left = EXTRUDE.
      const angle = Math.atan2(domX, -domY);
      const a = Math.abs(angle);
      let next: ShapeMode;
      if (a <= Math.PI / 4) next = "box";
      else if (a >= (3 * Math.PI) / 4) next = "circle";
      else if (angle > 0) next = "voxel";
      else next = "extrude";
      if (next !== dialPendingShape) {
        dialPendingShape = next;
        paintShapeDial(next);
        const idx = dominantControllerIndex();
        if (idx >= 0) pulseHaptic(idx, 0.4, 15);
      }
      const c = clampToUnitDisc(domX, domY);
      shapeDial.dot.position.set(
        c.x * DIAL_DOT_MAX_OFFSET,
        -c.y * DIAL_DOT_MAX_OFFSET,
        0.001,
      );
    } else {
      setShapeMode(dialPendingShape);
      shapeDial.group.visible = false;
      currentDial = null;
    }
  } else if (currentDial === "selectAction") {
    if (domActive) {
      const next: SelectAction = domX > 0 ? "delete" : "move";
      if (next !== dialPendingSelectAction) {
        dialPendingSelectAction = next;
        paintSelectActionDial(next);
        const idx = dominantControllerIndex();
        if (idx >= 0) pulseHaptic(idx, 0.4, 15);
      }
      const c = clampToUnitDisc(domX, domY);
      selectActionDial.dot.position.set(
        c.x * DIAL_DOT_MAX_OFFSET,
        -c.y * DIAL_DOT_MAX_OFFSET,
        0.001,
      );
    } else {
      setSelectAction(dialPendingSelectAction);
      selectActionDial.group.visible = false;
      currentDial = null;
    }
  }
}

// ── Grab Bar Perch ───────────────────────────────────────────────────────
//
// Each frame:
//   1. Rotate grabBarGroup so the bar lives on whichever side of the cube
//      faces the user (computed in gridGroup-local so the angle is
//      invariant to world.rotation).
//   2. Push the bar out far enough that it clears the cube's silhouette
//      even when a corner faces the user — projection along the user
//      direction is halfX·|sin θ| + halfZ·|cos θ|, plus a constant
//      STANDOFF buffer.
//   3. Scale grabBarGroup by 1/world.scale so the bar's geometry renders
//      at its design size regardless of canvas pinch-scale.
//   4. Tilt grabBarMesh via lookAt so its +Z face also angles vertically
//      toward the headset, not just yaws horizontally. lookAt makes the
//      mesh's -Z face the target; passing `2·bar − user` mirrors the
//      user across the bar's centre so +Z ends up facing the user.
//
// Tracking gate: re-pose only while the user is actively driving the
// canvas (canvas_drag, two-grip nav, or the just-released frame of
// either). Outside those gestures the bar holds its last orientation so
// subtle head turns don't slide it around the cube.

type GrabBarPerch = "front-bottom" | "top-back" | "bottom-back";
const _grabBarUserTmp = new THREE.Vector3();
const _grabBarLookTmp = new THREE.Vector3();
const _grabBarCamLocalTmp = new THREE.Vector3();
let grabBarPositioned = false;
let prevBothSqueezingGrabBar = false;
let prevInCanvasDragGrabBar = false;
let prevGrabBarPerch: GrabBarPerch = "front-bottom";

// Bar's apparent angular width as a fraction of its distance from the
// user's head (~width / distance). At 0.1 the bar subtends about 5.7°
// of view. Tweak to taste; the bar holds this size at every distance.
const GRAB_BAR_APPARENT_RATIO = 0.1;
// Floor on the bar's cumulative scale (world.scale × gridGroup.scale ×
// grabBarGroup.scale). Below this the apparent-size compensation
// generates massive grabBarMesh.position values (worldOffset divided by
// a tiny cumulative), and the bar's world position becomes precision-
// limited — which feeds back into canvas-drag as jitter. Clamping here
// means the bar stops shrinking at close range and instead appears
// proportionally larger; the math stays well-conditioned.
const MIN_GRAB_BAR_CUMULATIVE = 0.1;

function updateGrabBar(): void {
  const bothSqueezing =
    controllerStates[0].squeezing && controllerStates[1].squeezing;
  const inCanvasDrag = interaction.kind === "canvas_drag";
  const justReleasedNav = prevBothSqueezingGrabBar && !bothSqueezing;
  const justReleasedDrag = prevInCanvasDragGrabBar && !inCanvasDrag;
  prevBothSqueezingGrabBar = bothSqueezing;
  prevInCanvasDragGrabBar = inCanvasDrag;
  const trackingActive =
    !grabBarPositioned ||
    bothSqueezing ||
    justReleasedNav ||
    inCanvasDrag ||
    justReleasedDrag;

  // Sample head world position once for the frame — used for yaw,
  // perch, distance-based size, and the lookAt tilt.
  const headCam = renderer.xr.isPresenting
    ? renderer.xr.getCamera()
    : camera;
  headCam.getWorldPosition(_grabBarUserTmp);
  _grabBarLookTmp.copy(_grabBarUserTmp);

  // Yaw re-poses to face the user every frame — including during
  // canvas_drag, so the bar slides around to whichever side of the
  // cube is currently facing the user instead of being stuck on the
  // press-time side. handleCanvasDrag reads grabBarMesh.getWorldPosition
  // after this runs and adjusts world.position to keep the bar under
  // the laser, so yaw-driven motion of the bar is absorbed into the
  // canvas translation rather than producing drift. The atan2-near-
  // origin feedback risk that originally motivated freezing yaw here
  // is bounded by the MIN_CAM_TO_BAR clamp in handleCanvasDrag, which
  // refuses to bring the bar inside the user's personal-space radius.
  if (trackingActive) {
    _grabBarCamLocalTmp.copy(_grabBarUserTmp);
    gridGroup.worldToLocal(_grabBarCamLocalTmp);
    const localUserX = _grabBarCamLocalTmp.x;
    const localUserZ = _grabBarCamLocalTmp.z;
    if (localUserX * localUserX + localUserZ * localUserZ >= 1e-6) {
      grabBarGroup.rotation.y = Math.atan2(localUserX, localUserZ);
      grabBarPositioned = true;
    }
  }

  const theta = grabBarGroup.rotation.y;
  const scale = world.scale.x;
  const halfX = (volumeBounds.maxX - volumeBounds.minX) / 2;
  const halfZ = (volumeBounds.maxZ - volumeBounds.minZ) / 2;
  const projection =
    halfX * Math.abs(Math.sin(theta)) + halfZ * Math.abs(Math.cos(theta));

  // Perch is frozen during canvas_drag so a vertical canvas swing can't
  // cross a floor/ceiling boundary mid-drag and yank the bar out from
  // under the user's laser. Releases on drag end.
  let perch: GrabBarPerch;
  if (inCanvasDrag) {
    perch = prevGrabBarPerch;
  } else {
    _grabBarCamLocalTmp.copy(_grabBarUserTmp);
    gridGroup.updateMatrixWorld(true);
    gridGroup.worldToLocal(_grabBarCamLocalTmp);
    const camY = _grabBarCamLocalTmp.y;
    if (camY < 0) perch = "bottom-back";
    else if (camY > volumeBounds.height) perch = "top-back";
    else perch = "front-bottom";
  }
  const perchChanged = perch !== prevGrabBarPerch;
  prevGrabBarPerch = perch;

  // Perch offset in WORLD units relative to gridGroup origin. The legacy
  // perch math wrote grabBarGroup-local numbers that turned into world
  // offsets only after being multiplied by gridGroup.scale (the bar's
  // old cumulative was `gridGroup.scale × 1/world.scale × world.scale =
  // gridGroup.scale`). Reproduce that here so the bar still lands where
  // the perch wants it; we'll separately divide by the new cumulative
  // to recover the local position below.
  const gs = gridGroup.scale.x;
  let worldOffsetY: number;
  let worldOffsetZ: number;
  if (perch === "front-bottom") {
    worldOffsetZ = (scale * projection + GRAB_BAR_STANDOFF) * gs;
    worldOffsetY = -GRAB_BAR_FLOOR_GAP * gs;
  } else if (perch === "top-back") {
    worldOffsetZ = -(scale * projection + GRAB_BAR_STANDOFF) * gs;
    worldOffsetY = (scale * volumeBounds.height + GRAB_BAR_CEILING_GAP) * gs;
  } else {
    worldOffsetZ = -(scale * projection + GRAB_BAR_STANDOFF) * gs;
    worldOffsetY = -GRAB_BAR_FLOOR_GAP * gs;
  }

  // Apparent-size compensation. cumulative_scale × GRAB_BAR_WIDTH is the
  // bar's world width. We want that = distance × GRAB_BAR_APPARENT_RATIO.
  // Distance uses the previous frame's bar world position; the one-frame
  // lag converges as the user/canvas move smoothly. Clamp distance to a
  // safe minimum so the bar doesn't collapse if the user puts their head
  // on top of it.
  grabBarMesh.getWorldPosition(_grabBarWorldPosTmp);
  const distToBar = Math.max(
    0.1,
    _grabBarUserTmp.distanceTo(_grabBarWorldPosTmp),
  );
  const desiredCumulative = Math.max(
    MIN_GRAB_BAR_CUMULATIVE,
    (distToBar * GRAB_BAR_APPARENT_RATIO) / GRAB_BAR_WIDTH,
  );
  // grabBarGroup.scale × world.scale × gridGroup.scale = cumulative.
  grabBarGroup.scale.setScalar(
    desiredCumulative / (world.scale.x * gridGroup.scale.x),
  );
  // local_pos × cumulative = world_pos. Convert the perch's world-units
  // offset back into grabBarGroup-local so the bar lands at the same
  // world spot regardless of how cumulative came out.
  grabBarMesh.position.y = worldOffsetY / desiredCumulative;
  grabBarMesh.position.z = worldOffsetZ / desiredCumulative;

  if (trackingActive || perchChanged) {
    grabBarGroup.updateMatrixWorld(true);
    grabBarMesh.lookAt(_grabBarLookTmp);
  }
}

// ── AR layout tabletop ─────────────────────────────────────────────────────
// "VR edit" from Layout mode: instead of the model-on-a-table voxel experience,
// shrink the WHOLE layout (walls + placed models) onto the desk in front of you
// and show it through passthrough AR. It's a static diorama — you lean in and
// walk around it physically; no teleport, no modeling tools, no grab bar.
const _ttSaved = {
  pos: new THREE.Vector3(),
  quat: new THREE.Quaternion(),
  scale: new THREE.Vector3(),
};
// The longer horizontal span of the layout maps to this many metres (~2 ft —
// matches the voxel model-on-a-table footprint so both VR experiences read at a
// comparable desktop scale).
const TABLETOP_FOOTPRINT = 0.6;
const TABLETOP_DESK_HEIGHT = 0.72; // metres above the real floor: the layout's floor plane sits here
const TABLETOP_FORWARD = 0.5; // metres in front of the player (along −Z, like VR_GRID_POSITION)
const TABLETOP_EMPTY_SPAN = 4; // metres — fallback span so an empty layout still gets a sane scale

function enterLayoutTabletop(): void {
  setState("layoutTabletopActive", true);
  // See-through walls track the headset each frame (the tabletop render branch
  // calls updateOcclusionFade), so the wall pixels between you and the room
  // interior fade just like the desktop 3D view — no need to force them solid.
  floorScene.setDotField(false); // the metres dot-sea is meaningless shrunk to a desk
  for (const ray of controllerRays) hideControllerRay(ray); // no modeling laser
  _ttSaved.pos.copy(floorScene.root.position);
  _ttSaved.quat.copy(floorScene.root.quaternion);
  _ttSaved.scale.copy(floorScene.root.scale);

  // Center + scale the layout onto the desk. The root transform is uniform
  // scale `s` + translation `P` (rotation reset), so a floor-local point p maps
  // to world P + s·p. Solve P so the layout's footprint center lands in front of
  // the player at desk height, with the layout floor (local y=0) AT the desk top.
  const box = layoutBounds();
  let cx = 0;
  let cz = 0;
  let span = TABLETOP_EMPTY_SPAN;
  if (box) {
    cx = (box.min.x + box.max.x) / 2;
    cz = (box.min.z + box.max.z) / 2;
    span = Math.max(box.max.x - box.min.x, box.max.z - box.min.z, 0.5);
  }
  const s = TABLETOP_FOOTPRINT / span;
  floorScene.root.scale.setScalar(s);
  floorScene.root.quaternion.identity();
  floorScene.root.position.set(
    -s * cx,
    TABLETOP_DESK_HEIGHT,
    -TABLETOP_FORWARD - s * cz,
  );
  floorScene.root.visible = true;

  // Give the layout a floor to stand on (it's just walls + models otherwise,
  // floating over the passthrough desk). Prefer a real floor bounded by the
  // room's walls; fall back to the soft ground disc only when the layout has no
  // closed room for that footprint to fill. The disc is full-strength out to 55%
  // of its radius, so ~1.5× the layout span keeps the whole footprint (corners
  // included) on solid floor and fades it out beyond — in floor-local metres so
  // it scales with the layout.
  const hasRoomFloor = wallSystem.setRoomFloor(true);
  floorScene.setGroundDisc(!hasRoomFloor, cx, cz, span * 1.5);

  // Hand the dual-palm-grip nav gesture (handleNavigation) the layout: rotate /
  // zoom / pan floorScene.root instead of the (hidden) voxel canvas, pivoting on
  // the layout's footprint center. Scale bounds are relative to the tabletop's
  // own scale `s` so the diorama can't be pinched into oblivion or blown up to
  // fill the room.
  setNavContext(
    floorScene.root,
    (out) => {
      out.set(cx, 0, cz);
      floorScene.root.localToWorld(out);
    },
    s * 0.25,
    s * 6,
  );
}

function exitLayoutTabletop(): void {
  setState("layoutTabletopActive", false);
  resetNavContext();
  wallSystem.setRoomFloor(false);
  floorScene.setGroundDisc(false);
  floorScene.setDotField(true);
  floorScene.root.position.copy(_ttSaved.pos);
  floorScene.root.quaternion.copy(_ttSaved.quat);
  floorScene.root.scale.copy(_ttSaved.scale);
}

// ── VR layout walkthrough ──────────────────────────────────────────────────
// First-person walk of the floor plan in VR: the room renders at full (1:1)
// scale and the player stands in it, moving by point-and-teleport. We move the
// WORLD (floorScene.root) around an origin-fixed player rather than touching the
// XR reference space — head tracking still works, and a teleport just slides the
// room so the aimed spot lands under the player.
// The active flag lives in uiState ("walkthroughActive") so main's
// presentation-lighting rig can subscribe to enter/exit.
const _wtSaved = {
  pos: new THREE.Vector3(),
  quat: new THREE.Quaternion(),
  scale: new THREE.Vector3(),
};
const WT_ARC_SEGMENTS = 24;
const _wtArcGeo = new THREE.BufferGeometry();
_wtArcGeo.setAttribute(
  "position",
  new THREE.BufferAttribute(new Float32Array((WT_ARC_SEGMENTS + 1) * 3), 3),
);
const teleportArc = new THREE.Line(
  _wtArcGeo,
  // toneMapped:false — exact UI blue; these only show while the walkthrough's
  // ACES presentation rig is active.
  new THREE.LineBasicMaterial({ color: 0x6fa0ff, depthTest: false, toneMapped: false }),
);
teleportArc.frustumCulled = false;
teleportArc.renderOrder = 998;
teleportArc.visible = false;
scene.add(teleportArc);
const teleportMarker = new THREE.Mesh(
  new THREE.RingGeometry(0.18, 0.28, 32).rotateX(-Math.PI / 2),
  new THREE.MeshBasicMaterial({
    color: 0x6fa0ff,
    transparent: true,
    opacity: 0.85,
    depthTest: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  }),
);
teleportMarker.renderOrder = 999;
teleportMarker.visible = false;
scene.add(teleportMarker);

let _wtAiming = false;
let _wtLandingValid = false;
const _wtLanding = new THREE.Vector3();
const _wtLocal = new THREE.Vector3();
const _wtTmpV = new THREE.Vector3();
const _wtTmpQ = new THREE.Quaternion();
const _wtFwd = new THREE.Vector3();
const _wtUp = new THREE.Vector3(0, 1, 0);
// Snap turn re-arms only after the stick recenters, so one flick = one turn.
let _wtTurnArmed = true;

// ── Vision Pro hand-tracking state ─────────────────────────────────────────
// AVP has no thumbsticks; walkthrough locomotion there comes from hand gestures
// read off the live XRFrame. Index 0 = left hand, 1 = right.
//   • one-hand pinch  → aim + fire the teleport arc (same arc as the sticks)
//   • two-hand pinch  → twist to snap-turn in fixed steps
// A pinch is thumb-tip↔index-tip distance with hysteresis so a held pinch
// doesn't chatter at the threshold.
const HAND_PINCH_ON = 0.02; // m — below this starts a pinch
const HAND_PINCH_OFF = 0.04; // m — must reopen past this to release
const _handPinching = [false, false]; // latched pinch state per hand
const _handPresent = [false, false]; // hand tracked this frame?
const _handHasAim = [false, false]; // got a usable pointer ray this frame?
const _handAimOrigin = [new THREE.Vector3(), new THREE.Vector3()];
const _handAimFwd = [new THREE.Vector3(), new THREE.Vector3()];
const _handPinchPt = [new THREE.Vector3(), new THREE.Vector3()]; // thumb/index midpoint
const _hThumb = new THREE.Vector3();
const _hIndex = new THREE.Vector3();
const _hWrist = new THREE.Vector3();
const _hQuat = new THREE.Quaternion();
// Two-hand twist-turn: heading of the L→R line, locked when both first pinch
// and advanced one step each time we snap.
let _wtTwisting = false;
let _wtTwistRef = 0; // radians

function enterWalkthrough(): void {
  setState("walkthroughActive", true);
  // Walls must be SOLID here, and this is the only place that can make them
  // so: the walkthrough render branch never runs updateFloorFrame, so
  // whatever see-through state the desktop 3D view left on the walls would
  // stay FROZEN all session — occMat with the old desktop camera in its
  // uniforms, i.e. a static "sky window" painted onto whichever walls stood
  // in front of the room when the session started.
  wallSystem.clearOcclusionFade();
  // Foveated rendering: another fill-rate lever for the presentation rig's
  // per-pixel cost (the editor keeps 0 for edge-to-edge sharpness).
  renderer.xr.setFoveation(1);
  _wtSaved.pos.copy(floorScene.root.position);
  _wtSaved.quat.copy(floorScene.root.quaternion);
  _wtSaved.scale.copy(floorScene.root.scale);
  floorScene.root.visible = true;
  floorScene.root.scale.setScalar(1); // full real-world scale
  gridGroup.visible = false; // hide the voxel model
  grabBarGroup.visible = false;
  for (const ray of controllerRays) hideControllerRay(ray); // no modeling laser
  positionAtDoor();
}

function exitWalkthrough(): void {
  setState("walkthroughActive", false);
  renderer.xr.setFoveation(0); // restore the editor's edge-to-edge sharpness
  _wtAiming = false;
  _wtTwisting = false;
  _handPinching[0] = false;
  _handPinching[1] = false;
  teleportArc.visible = false;
  teleportMarker.visible = false;
  floorScene.root.position.copy(_wtSaved.pos);
  floorScene.root.quaternion.copy(_wtSaved.quat);
  floorScene.root.scale.copy(_wtSaved.scale);
}

// Slide + rotate the floor so a chosen door sits under the player, facing into
// the room (the room interior toward the camera's forward, −Z). If there are no
// doors, the player just starts at the floor origin.
function positionAtDoor(): void {
  let ex = 0;
  let ez = 0;
  let theta = 0;
  const door = wallSystem.doorList()[0];
  if (door) {
    const g = wallSystem.openingGeometry(door.wallId, door.openingId);
    if (g) {
      // Two candidate normals ⟂ the wall; keep the one pointing into the room.
      const n1x = -g.uz;
      const n1z = g.ux;
      const inside = wallSystem.isInsideRoom(g.cx + n1x * 0.5, g.cz + n1z * 0.5);
      const nx = inside ? n1x : -n1x;
      const nz = inside ? n1z : -n1z;
      ex = g.cx;
      ez = g.cz;
      // Rotate the floor so the inward normal lands on world −Z (camera forward):
      // a floor dir at angle atan2(nx,nz) from +Z should map to world angle π.
      theta = Math.PI - Math.atan2(nx, nz);
    }
  }
  floorScene.root.position.set(0, 0, 0);
  floorScene.root.rotation.set(0, theta, 0);
  floorScene.root.updateMatrixWorld(true);
  // Translate so the entry point maps to the world origin (under the player).
  _wtTmpV.set(ex, 0, ez);
  floorScene.root.localToWorld(_wtTmpV);
  floorScene.root.position.set(-_wtTmpV.x, 0, -_wtTmpV.z);
}

// Per-frame teleport: aim a parabola from whichever controller's joystick is
// pushed; on release, jump the player to the (valid, in-room) landing spot.
const WT_GRAVITY = 9.8;
const WT_LAUNCH = 7; // m/s — pitch the controller up to reach farther
// Snap turn: a sideways stick flick spins the player a fixed increment.
// Instantaneous and incremental (one step per flick) — the player stands at the
// origin, so we rotate the WORLD about the vertical axis under their head, the
// same trick teleport uses to slide it.
const WT_SNAP_DEG = 30;
const WT_SNAP_ON = 0.7; // |x| past this fires a turn
const WT_SNAP_OFF = 0.35; // must fall back under this to re-arm the next turn
function wtSnapTurn(playerDeg: number): void {
  // The world turns opposite the player, about the vertical axis under the head.
  const rad = THREE.MathUtils.degToRad(-playerDeg);
  renderer.xr.getCamera().getWorldPosition(_wtTmpV);
  const px = _wtTmpV.x;
  const pz = _wtTmpV.z;
  _wtTmpQ.setFromAxisAngle(_wtUp, rad);
  floorScene.root.position.x -= px;
  floorScene.root.position.z -= pz;
  floorScene.root.position.applyQuaternion(_wtTmpQ); // about world Y → y unchanged
  floorScene.root.position.x += px;
  floorScene.root.position.z += pz;
  floorScene.root.quaternion.premultiply(_wtTmpQ);
  // The sun rides inside root, so the world→light shadow matrix is now stale
  // (see the teleport branch) — re-bake it once.
  renderer.shadowMap.needsUpdate = true;
}
// Aim the teleport parabola from a world-space origin along a world-space
// forward: redraw the arc + landing ring and record whether the spot is inside
// the room. Shared by the stick (Quest) and hand (Vision Pro) paths.
function wtAimFrom(origin: THREE.Vector3, fwd: THREE.Vector3): void {
  const oy = origin.y;
  const vy = fwd.y * WT_LAUNCH;
  const disc = vy * vy + 2 * WT_GRAVITY * oy;
  if (disc < 0) return; // no ground intercept (pointing up) — keep the last arc
  const tLand = (vy + Math.sqrt(disc)) / WT_GRAVITY; // time to reach y=0
  const pos = _wtArcGeo.attributes.position as THREE.BufferAttribute;
  for (let s = 0; s <= WT_ARC_SEGMENTS; s++) {
    const t = (s / WT_ARC_SEGMENTS) * tLand;
    pos.setXYZ(
      s,
      origin.x + fwd.x * WT_LAUNCH * t,
      origin.y + fwd.y * WT_LAUNCH * t - 0.5 * WT_GRAVITY * t * t,
      origin.z + fwd.z * WT_LAUNCH * t,
    );
  }
  pos.needsUpdate = true;
  _wtLanding.set(
    origin.x + fwd.x * WT_LAUNCH * tLand,
    0,
    origin.z + fwd.z * WT_LAUNCH * tLand,
  );
  _wtLocal.copy(_wtLanding);
  floorScene.root.worldToLocal(_wtLocal);
  _wtLandingValid = wallSystem.isInsideRoom(_wtLocal.x, _wtLocal.z);
  teleportArc.visible = true;
  teleportMarker.visible = true;
  teleportMarker.position.copy(_wtLanding);
  const col = _wtLandingValid ? 0x6fa0ff : 0xc0445a;
  (teleportArc.material as THREE.LineBasicMaterial).color.setHex(col);
  (teleportMarker.material as THREE.MeshBasicMaterial).color.setHex(col);
}

// Commit the currently-aimed teleport: hide the arc and, if the landing is
// valid, slide the room so that spot ends up under the player's head.
function wtCommitTeleport(): void {
  teleportArc.visible = false;
  teleportMarker.visible = false;
  if (_wtLandingValid) {
    renderer.xr.getCamera().getWorldPosition(_wtTmpV);
    floorScene.root.position.x += _wtTmpV.x - _wtLanding.x;
    floorScene.root.position.z += _wtTmpV.z - _wtLanding.z;
    // The sun rides inside root, so the shadow-map CONTENT stays valid — but
    // three freezes the shadow MATRIX (world→light) while autoUpdate is off.
    // The world just moved, so re-bake once or every surface samples the map
    // through a stale transform and the lighting falls apart.
    renderer.shadowMap.needsUpdate = true;
  }
  _wtLandingValid = false;
}

// ── Quest controllers: thumbstick teleport + snap-turn ──
function updateWalkthroughSticks(): void {
  let bestDefl = 0;
  let aimIdx = -1;
  let turnX = 0; // strongest horizontal-dominant deflection across controllers
  for (let i = 0; i < 2; i++) {
    const gp = controllerStates[i].inputSource?.gamepad;
    if (!gp) continue;
    const ax = gp.axes[2] ?? 0;
    const ay = gp.axes[3] ?? 0;
    // Split by dominant axis so one stick does both: a sideways push turns, a
    // forward/back push aims the teleport — no cross-talk between them.
    if (Math.abs(ax) > Math.abs(ay)) {
      if (Math.abs(ax) > Math.abs(turnX)) turnX = ax;
    } else {
      const d = Math.hypot(ax, ay);
      if (d > bestDefl) {
        bestDefl = d;
        aimIdx = i;
      }
    }
  }

  // Snap turn — instantaneous, one increment per flick. Push right → turn right.
  if (_wtTurnArmed && Math.abs(turnX) > WT_SNAP_ON) {
    wtSnapTurn(turnX > 0 ? -WT_SNAP_DEG : WT_SNAP_DEG);
    _wtTurnArmed = false;
  } else if (Math.abs(turnX) < WT_SNAP_OFF) {
    _wtTurnArmed = true;
  }

  if (!_wtAiming && bestDefl > 0.6 && aimIdx >= 0) _wtAiming = true;
  if (_wtAiming && aimIdx >= 0) {
    const ctrl = controllers[aimIdx];
    ctrl.updateMatrixWorld();
    ctrl.getWorldPosition(_wtTmpV);
    ctrl.getWorldQuaternion(_wtTmpQ);
    _wtFwd.set(0, 0, -1).applyQuaternion(_wtTmpQ); // controller pointing dir
    wtAimFrom(_wtTmpV, _wtFwd);
  }
  if (_wtAiming && bestDefl < 0.25) {
    _wtAiming = false;
    wtCommitTeleport();
  }
}

// ── Vision Pro hands: pinch-to-teleport + two-hand twist-to-turn ──
// Reads joint poses off the live XRFrame. Runs alongside the stick path: a hand
// reports no gamepad and a controller reports no joints, so on any given device
// only one of the two does anything.
function updateWalkthroughHands(): void {
  const session = renderer.xr.getSession();
  const frame = renderer.xr.getFrame();
  const refSpace = renderer.xr.getReferenceSpace();
  if (!session || !frame || !refSpace || !frame.getJointPose) return;

  _handPresent[0] = false;
  _handPresent[1] = false;
  for (const src of session.inputSources) {
    const hand = src.hand;
    if (!hand) continue;
    const h = src.handedness === "left" ? 0 : 1;
    const thumbJoint = hand.get("thumb-tip");
    const indexJoint = hand.get("index-finger-tip");
    if (!thumbJoint || !indexJoint) continue;
    const tp = frame.getJointPose(thumbJoint, refSpace);
    const ip = frame.getJointPose(indexJoint, refSpace);
    if (!tp || !ip) continue;
    _handPresent[h] = true;
    _hThumb.set(tp.transform.position.x, tp.transform.position.y, tp.transform.position.z);
    _hIndex.set(ip.transform.position.x, ip.transform.position.y, ip.transform.position.z);
    // Pinch with hysteresis.
    const dist = _hThumb.distanceTo(_hIndex);
    if (_handPinching[h]) {
      if (dist > HAND_PINCH_OFF) _handPinching[h] = false;
    } else if (dist < HAND_PINCH_ON) {
      _handPinching[h] = true;
    }
    _handPinchPt[h].copy(_hThumb).add(_hIndex).multiplyScalar(0.5);
    // Aim ray: prefer the platform's pointer ray (targetRaySpace); fall back to
    // wrist→pinch so a device that omits it can still teleport.
    const rayPose = frame.getPose(src.targetRaySpace, refSpace);
    if (rayPose) {
      _handAimOrigin[h].set(
        rayPose.transform.position.x,
        rayPose.transform.position.y,
        rayPose.transform.position.z,
      );
      _hQuat.set(
        rayPose.transform.orientation.x,
        rayPose.transform.orientation.y,
        rayPose.transform.orientation.z,
        rayPose.transform.orientation.w,
      );
      _handAimFwd[h].set(0, 0, -1).applyQuaternion(_hQuat);
      _handHasAim[h] = true;
    } else {
      const wristJoint = hand.get("wrist");
      const wp = wristJoint && frame.getJointPose(wristJoint, refSpace);
      if (wp) {
        _hWrist.set(wp.transform.position.x, wp.transform.position.y, wp.transform.position.z);
        _handAimOrigin[h].copy(_handPinchPt[h]);
        _handAimFwd[h].copy(_handPinchPt[h]).sub(_hWrist).normalize();
        _handHasAim[h] = true;
      } else {
        _handHasAim[h] = false;
      }
    }
  }
  // Drop pinch/aim for any hand that vanished (tracking loss / out of frustum).
  for (let h = 0; h < 2; h++) {
    if (!_handPresent[h]) {
      _handPinching[h] = false;
      _handHasAim[h] = false;
    }
  }

  // No hands tracked this frame (the Quest controller path reports gamepads,
  // not joints) — bail before touching teleport state. Otherwise the
  // pinchCount===0 release branch below reads the STICK path's _wtAiming (set
  // moments ago in updateWalkthroughSticks) as a just-released pinch and fires
  // wtCommitTeleport every frame, hiding the arc the stick just drew before it
  // ever renders. That is why stick teleport shows no arc at all on Quest.
  if (!_handPresent[0] && !_handPresent[1]) return;

  const pinchCount = (_handPinching[0] ? 1 : 0) + (_handPinching[1] ? 1 : 0);

  if (pinchCount === 2) {
    // Two-hand twist → stepped turn. The second pinch reinterprets the gesture,
    // so cancel any teleport the first hand was aiming.
    if (_wtAiming) {
      _wtAiming = false;
      teleportArc.visible = false;
      teleportMarker.visible = false;
    }
    const heading = Math.atan2(
      _handPinchPt[1].z - _handPinchPt[0].z,
      _handPinchPt[1].x - _handPinchPt[0].x,
    );
    if (!_wtTwisting) {
      _wtTwisting = true;
      _wtTwistRef = heading;
    } else {
      const step = THREE.MathUtils.degToRad(WT_SNAP_DEG);
      let delta = heading - _wtTwistRef; // shortest-path change since the lock
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      // Ratchet: each full step of physical twist snaps the view one increment.
      // (Twist direction → turn direction is best-effort; flip the two signs
      // below if it turns the wrong way on the headset.)
      while (delta >= step) {
        wtSnapTurn(-WT_SNAP_DEG);
        _wtTwistRef += step;
        delta -= step;
      }
      while (delta <= -step) {
        wtSnapTurn(WT_SNAP_DEG);
        _wtTwistRef -= step;
        delta += step;
      }
    }
    return;
  }

  _wtTwisting = false;

  if (pinchCount === 1) {
    // One-hand pinch → aim the teleport arc from that hand.
    const h = _handPinching[1] ? 1 : 0;
    if (_handHasAim[h]) {
      _wtAiming = true;
      wtAimFrom(_handAimOrigin[h], _handAimFwd[h]);
    }
  } else if (_wtAiming) {
    // Released the single pinch → jump.
    _wtAiming = false;
    wtCommitTeleport();
  }
}

function updateWalkthrough(): void {
  updateWalkthroughSticks();
  updateWalkthroughHands();
}

  return {
    // Session + walkthrough.
    vrButton,
    requestVRSession,
    walkthroughOn: () => uiState.walkthroughActive,
    layoutTabletopOn: () => uiState.layoutTabletopActive,
    updateWalkthrough,
    // Per-frame VR updates (render loop, immersive branch).
    updatePickAndVisuals,
    pollFaceButtons,
    pollUndoRedo,
    pollDials,
    updateUIPanelsLayout,
    updateGrabBar,
    // Panel summon + grab bar hooks for handlePanelClick / canvas resets.
    summonPanelAtGaze,
    clearPanelSummon: () => {
      panelSummonOwner = null;
    },
    resetGrabBarPosition: () => {
      grabBarPositioned = false;
    },
  };
}
